/**
 *  Map script file
**/

import layerService, { tooltipStyle } from '@/api/layerService'
import layerids from '@/api/layerids'
import hex2rgb from '@/utils/hex2rgb'
import LabeledGeoJsonLayer from '@/api/LabeledGeojsonLayer'
import { mapState, mapMutations } from 'vuex'
import { MODULE_NAME, MUTATIONS } from '@/store/map/constants'
import { MODULE_NAME as MODULE_NAME_FILTERS, MUTATIONS as MUTATIONS_FILTERS } from '@/store/filters/constants'
import queries from '@/api/queries'

export default {
  name: 'Map',
  mixins: [],
  components: {
    MapComponent: () => import('@/components/map-component/MapComponent.vue'),
    Header: () => import('@/components/header/Header.vue'),
    MapSidebar: () => import('@/components/map-sidebar/MapSidebar.vue')
  },
  props: {},
  data () {
    return {
      mapLoaded: false,
      sidebarExpanded: true,
      ...layerids
    }
  },
  computed: {
    ...mapState(MODULE_NAME, ['viewState']),
    ...mapState(MODULE_NAME_FILTERS, ['filters']),
    mapExpanded () {
      return !this.sidebarExpanded
    },
    viewportFilterEnabled () {
      return this.filters.find(f => f.key === 'viewport').enabled
    },
    centerId () {
      return this.$route.params.id
    }
  },
  watch: {
    'viewState.zoom' (zoom, oldZoom) {
      if ((zoom || 0).toFixed(1) !== (oldZoom || 0).toFixed(1)) {
        layerService.updateLayer(this.clusterLayerId, {
          pointRadiusScale: layerService.getZoomFactor()
        })
      }
    },
    viewportFilterEnabled (flag) {
      if (flag) {
        this.setViewportShops()
      }
    },
    centerId () {
      this.updateCentersLayer()
    }
  },
  beforeDestroy () {
    layerService.removeLayer(this.clusterLayerId)
  },
  methods: {
    ...mapMutations(MODULE_NAME, [MUTATIONS.FLY_TO]),
    ...mapMutations(MODULE_NAME_FILTERS, [MUTATIONS_FILTERS.SET_FILTER_VALUE]),
    resize () {
      this.sidebarExpanded = !this.sidebarExpanded
    },
    async onMapLoaded () {
      await this.drawCentersLayer()
      this.mapLoaded = true
    },
    async drawCentersLayer () {
      await layerService.addSQLLayer({
        id: this.clusterLayerId,
        sql: queries.centers,
        sublayer: LabeledGeoJsonLayer,
        stroked: true,
        filled: true,
        getLineColor: [255, 255, 255],
        getFillColor: () => {
          const grey = hex2rgb('#9397a2')
          const green = hex2rgb('#16969d')
          return this.centerId ? grey : green
        },
        updateTriggers: {
          getFillColor: [this.centerId]
        },
        getLabelSize: 10,
        pickable: true,
        zIndex: 2,
        getTooltip: ({ object, x, y }) => {
          const count = Number(object.properties._cdb_feature_count) || 0
          const name = object.properties.local
          const text = count > 1 ? `${count} centros` : name
          return { text, style: tooltipStyle }
        },
        getLabel: d => {
          const value = Number(d.properties._cdb_feature_count || 0)
          return value > 1 ? String(value) : ' '
        },
        getRadius: d => {
          const value = d.properties._cdb_feature_count || 0
          return value > 1 ? 14 : 7
        },
        onClick: ({ object, coordinate }) => {
          if (!object) {
            return
          }
          const { _cdb_feature_count: count, center_id: id } = object.properties
          if (count === 1) {
            this.navigateShopDetail(id)
          } else {
            const [longitude, latitude] = coordinate
            this[MUTATIONS.FLY_TO]({
              longitude,
              latitude,
              zoom: this.viewState.zoom + 1
            })
          }
        },
        onViewportLoad: () => {
          if (this.viewportFilterEnabled) {
            this.setViewportShops()
          }
        },
        pointRadiusScale: layerService.getZoomFactor(),
        lineWidthMinPixels: 3,
        lineWidthMaxPixels: 3,
        labelLineHeight: 1,
        fontWeight: 500,
        fontFamily: 'Inter, sans-serif',
        requestOptions: {
          aggregation: {
            placement: 'centroid',
            resolution: 64,
            threshold: 1,
            columns: {
              local: {
                aggregate_function: 'mode',
                aggregated_column: 'local'
              },
              center_id: {
                aggregate_function: 'min',
                aggregated_column: 'center_id'
              }
            }
          }
        }
      })
    },
    async updateCentersLayer () {
      const sql = this.centerId ? queries.centers_exclude(this.centerId) : queries.centers
      await layerService.updateSQLLayer({ id: this.clusterLayerId, sql })
      layerService.updateLayer(this.clusterLayerId, {
        updateTriggers: {
          getFillColor: [this.centerId]
        }
      })
    },
    setViewportShops () {
      const filtered = layerService.getViewportFeatures(this.clusterLayerId)
      this[MUTATIONS_FILTERS.SET_FILTER_VALUE]({
        key: 'viewport',
        value: filtered.map(feat => {
          const count = feat.properties._cdb_feature_count
          const label = feat.properties.local
          const text = count > 1 ? `${count} centros` : label
          return {
            ...feat.properties,
            local: text
          }
        }).sort((a, b) => {
          return b._cdb_feature_count - a._cdb_feature_count
        })
      })
    },
    navigateShopDetail (id) {
      const isSameRoute = this.$route.name === 'shop-detail' &&
        this.$route.params.id === id
      if (!isSameRoute) {
        this.$router.push({
          name: 'shop-detail',
          params: { id }
        })
      }
    }
  }
}
